import React from "react";
import { useTranslation } from "react-i18next";
import Modal from "components/Modal";
import Button from "components/Button";
import { IconCheck } from "icons";
import { PaywallContent } from "./WarningSnackbar.types";
import CreditShortfallInfo from "./CreditShortfallInfo";

interface PaywallModalProps {
  content: PaywallContent;
  onClose: () => void;
  onCancel: () => void;
  onUpgrade: () => void;
  isNewPurchase: boolean;
  requiredCredits?: number | null;
  remainingCredits?: number | null;
}

const PaywallModal: React.FC<PaywallModalProps> = ({ content, onClose, onCancel, onUpgrade, isNewPurchase, requiredCredits = null, remainingCredits = null }) => {
  const { t } = useTranslation();

  return (
    <Modal onClose={onClose} show={true} bodyClassName="bg-black-100 !h-full !max-h-[600px] !max-w-[1180px]" className="!z-[99999]" backdropDisabled={true}>
      <div data-testid="paywall-modal" className="flex h-full gap-10 p-6 w-fit">
        {/* Left side - Image */}
        <div className="flex h-full">
          <img src={content.img} className="h-full object-contain rounded-xl bg-[#2B2B2B] max-w-[400px]" alt={content.title} />
        </div>

        {/* Right side - Content */}
        <div className="flex flex-col w-full justify-between max-w-[440px]">
          <div className="flex flex-col w-full gap-10 text-white overflow-hidden">
            <h2 className="text-h2 flex-shrink-0">{content.title}</h2>
            <span className="text-[17px] leading-[140%] tracking-[-0.255px] font-workSans flex-shrink-0">{content.description}</span>
            <CreditShortfallInfo requiredCredits={requiredCredits} remainingCredits={remainingCredits} tone="dark" />
            <div className="flex flex-col gap-3 overflow-hidden pb-3">
              <span className="text-[15px] leading-[140%] tracking-[-0.255px] font-workSans flex-shrink-0">{content.subtitle}</span>
              <ul className="flex flex-col gap-3 overflow-y-auto">
                {content.items.map((item, index) => (
                  <li key={index}>
                    <span className="flex items-center gap-[5px] text-h6 !font-normal">
                      <IconCheck className="w-5 h-5 text-purpleGreen flex-shrink-0" />
                      <b className="!font-semibold">{item.value}</b> {item.text}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button data-testid="paywall-cancel-btn" className="btn-secondary w-full" onClick={onCancel}>
              {t("billing:paywall_modal.cancel")}
            </Button>
            <Button data-testid="paywall-upgrade-btn" className="btn-primary w-full" onClick={onUpgrade}>
              {isNewPurchase ? t("billing:paywall_modal.buy_now") : t("billing:paywall_modal.upgrade_now")}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default PaywallModal;
